import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Mail, User, Phone, Hash, Building2, Search, Plus } from 'lucide-react';
import { config } from '@/config';
import AnimatedBackground from '@/components/AnimatedBackground';

interface Customer {
    cardCode: string;
    name: string;
}

const Register = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        email: '',
        name: '',
        phone: '',
        ssnCode: '',
        customer: null as Customer | null,
    });
    const [customerSearchQuery, setCustomerSearchQuery] = useState('');
    const [customerSearchResults, setCustomerSearchResults] = useState<Customer[]>([]);
    const [searchingCustomers, setSearchingCustomers] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [success, setSuccess] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!customerSearchQuery.trim()) {
            setCustomerSearchResults([]);
            return;
        }
        const timer = setTimeout(async () => {
            try {
                setSearchingCustomers(true);
                const response = await axios.get(`${config.backendUrl}/customers/search`, {
                    params: { searchString: customerSearchQuery },
                });
                setCustomerSearchResults(response.data || []);
            } catch {
                setCustomerSearchResults([]);
            } finally {
                setSearchingCustomers(false);
            }
        }, 500);
        return () => clearTimeout(timer);
    }, [customerSearchQuery]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setErrorMessage('');

        if (!formData.email || !formData.name || !formData.phone || !formData.ssnCode || !formData.customer) {
            setErrorMessage('Please fill in all required fields');
            return;
        }

        try {
            setLoading(true);
            await axios.post(`${config.backendUrl}/users/register`, {
                email: formData.email,
                name: formData.name,
                phone: formData.phone,
                ssnCode: formData.ssnCode,
                customer: formData.customer,
                role: 'external_sales',
            });
            setSuccess(true);
        } catch (error: any) {
            setErrorMessage(error.response?.data?.message || 'Registration failed');
        } finally {
            setLoading(false);
        }
    };

    const handleAddCustomer = (customer: Customer) => {
        setFormData((prev) => ({ ...prev, customer }));
        setCustomerSearchQuery('');
        setCustomerSearchResults([]);
    };

    if (success) {
        return (
            <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
                <AnimatedBackground />
                <div className="max-w-md w-full space-y-8 p-10 rounded-xl bg-white/90 backdrop-blur-sm shadow-xl">
                    <div className="text-center">
                        <h2 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-emerald-600 to-amber-500">
                            Registration Submitted
                        </h2>
                        <p className="mt-2 text-sm text-gray-600">
                            Your registration has been submitted. An admin will review your application and send you an invitation email to set your password once approved.
                        </p>
                        <button
                            type="button"
                            onClick={() => navigate('/login')}
                            className="mt-6 text-sm font-medium text-slate-800 hover:text-amber-500"
                        >
                            Back to Login
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
            <AnimatedBackground />
            <div className="max-w-md w-full space-y-8 p-10 rounded-xl bg-white/90 backdrop-blur-sm shadow-xl transform transition-all duration-300 hover:shadow-2xl">
                <div className="text-center">
                    <h2 className="mt-6 text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-slate-800 to-amber-500">
                        Create Account
                    </h2>
                    <p className="mt-2 text-sm text-gray-600">
                        Register for Foundation Elite rewards portal
                    </p>
                </div>

                <form className="mt-8 space-y-5" onSubmit={handleSubmit}>
                    <div className="group">
                        <div className="flex items-center space-x-3 p-3 rounded-lg border border-gray-200 group-focus-within:border-slate-800 group-focus-within:ring-1 group-focus-within:ring-amber-400 transition-all duration-200">
                            <Mail className="h-5 w-5 text-gray-400 shrink-0" />
                            <input
                                type="email"
                                required
                                className="flex-1 outline-none text-gray-900 placeholder-gray-500"
                                placeholder="Email *"
                                value={formData.email}
                                onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                            />
                        </div>
                    </div>

                    <div className="group">
                        <div className="flex items-center space-x-3 p-3 rounded-lg border border-gray-200 group-focus-within:border-slate-800 group-focus-within:ring-1 group-focus-within:ring-amber-400 transition-all duration-200">
                            <User className="h-5 w-5 text-gray-400 shrink-0" />
                            <input
                                type="text"
                                required
                                className="flex-1 outline-none text-gray-900 placeholder-gray-500"
                                placeholder="Name *"
                                value={formData.name}
                                onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                            />
                        </div>
                    </div>

                    <div className="group">
                        <div className="flex items-center space-x-3 p-3 rounded-lg border border-gray-200 group-focus-within:border-slate-800 group-focus-within:ring-1 group-focus-within:ring-amber-400 transition-all duration-200">
                            <Phone className="h-5 w-5 text-gray-400 shrink-0" />
                            <input
                                type="tel"
                                required
                                className="flex-1 outline-none text-gray-900 placeholder-gray-500"
                                placeholder="Phone *"
                                value={formData.phone}
                                onChange={(e) => setFormData((prev) => ({ ...prev, phone: e.target.value }))}
                            />
                        </div>
                    </div>

                    <div className="group">
                        <div className="flex items-center space-x-3 p-3 rounded-lg border border-gray-200 group-focus-within:border-slate-800 group-focus-within:ring-1 group-focus-within:ring-amber-400 transition-all duration-200">
                            <Hash className="h-5 w-5 text-gray-400 shrink-0" />
                            <input
                                type="text"
                                required
                                className="flex-1 outline-none text-gray-900 placeholder-gray-500"
                                placeholder="SSN Code *"
                                value={formData.ssnCode}
                                onChange={(e) => setFormData((prev) => ({ ...prev, ssnCode: e.target.value }))}
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Customer *</label>
                        {formData.customer ? (
                            <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-200">
                                <div className="flex items-center space-x-2">
                                    <Building2 className="h-4 w-4 text-amber-600" />
                                    <span className="font-medium text-amber-900">{formData.customer.name}</span>
                                    <span className="text-xs text-amber-600">({formData.customer.cardCode})</span>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setFormData((prev) => ({ ...prev, customer: null }))}
                                    className="text-amber-700 hover:text-amber-900 text-sm"
                                >
                                    Remove
                                </button>
                            </div>
                        ) : (
                            <>
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                                    <input
                                        type="text"
                                        value={customerSearchQuery}
                                        onChange={(e) => setCustomerSearchQuery(e.target.value)}
                                        placeholder="Search customers..."
                                        className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg outline-none focus:border-slate-800"
                                    />
                                </div>
                                {searchingCustomers && (
                                    <div className="mt-2 text-center">
                                        <div className="h-4 w-4 border-2 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto" />
                                    </div>
                                )}
                                {customerSearchResults.length > 0 && (
                                    <div className="mt-2 space-y-1 max-h-40 overflow-y-auto border border-gray-200 rounded-lg">
                                        {customerSearchResults.map((c) => (
                                            <button
                                                key={c.cardCode}
                                                type="button"
                                                onClick={() => handleAddCustomer(c)}
                                                className="w-full text-left p-3 hover:bg-amber-50 flex items-center justify-between"
                                            >
                                                <span className="font-medium">{c.name}</span>
                                                <span className="text-xs text-gray-500">({c.cardCode})</span>
                                                <Plus className="h-4 w-4 text-amber-600" />
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    {errorMessage && (
                        <div className="text-center text-red-500 text-sm">{errorMessage}</div>
                    )}

                    <div className="space-y-3">
                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-slate-800 to-slate-700 hover:from-slate-900 hover:to-amber-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-400 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {loading ? 'Submitting...' : 'Register'}
                        </button>
                        <button
                            type="button"
                            onClick={() => navigate('/login')}
                            className="w-full text-sm text-slate-600 hover:text-amber-500"
                        >
                            Back to Login
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default Register;
